import { request } from '../../api';

export type UnitKind = 'portal' | 'tunnel' | 'self_service' | 'vacuum' | 'water_treatment' | 'compressor' | 'other';
export type UnitProtocol = 'http' | 'mqtt' | 'fake';
export type CycleStatus = 'requested' | 'running' | 'completed' | 'failed' | 'aborted';

export interface Program {
  readonly code: string;
  readonly name: string;
  readonly durationSeconds: number;
}

export interface Reading {
  readonly metric: string;
  readonly value: number;
  readonly at: string;
}

export interface Forecast {
  readonly unitId: string;
  readonly risk: number;
  readonly driver: string | null;
  readonly daysToLimit: number | null;
  readonly computedAt: string;
}

export interface Unit {
  readonly id: string;
  readonly siteId: string;
  readonly code: string;
  readonly name: string;
  readonly kind: UnitKind;
  readonly vendor: string;
  readonly protocol: UnitProtocol;
  readonly endpoint: string | null;
  readonly programs: readonly Program[];
  readonly status: 'active' | 'maintenance' | 'retired';
  readonly lastSeenAt: string | null;
  readonly lastState: string | null;
  readonly readings: readonly Reading[];
  readonly forecast: Forecast | null;
}

export interface Cycle {
  readonly id: string;
  readonly unitId: string;
  readonly program: string;
  readonly status: CycleStatus;
  readonly requestedAt: string;
  readonly finishedAt: string | null;
  readonly error: string | null;
}

export interface UnitDraft {
  readonly siteId: string;
  readonly code: string;
  readonly name: string;
  readonly kind: UnitKind;
  readonly protocol: UnitProtocol;
  readonly endpoint?: string | null;
  readonly programs?: readonly Program[];
}

/** Пути вынесены константами: их сверяет тест с маршрутами сервера. */
export const EQUIPMENT_PATHS = {
  units: '/api/v1/ops/equipment/units',
  start: (id: string) => `/api/v1/ops/equipment/units/${encodeURIComponent(id)}/start`,
  cycles: '/api/v1/ops/equipment/cycles',
  forecastRun: '/api/v1/ops/equipment/forecast/run',
} as const;

export async function fetchUnits(siteId?: string): Promise<readonly Unit[]> {
  const query = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
  const data = await request<{ units: readonly Unit[] }>('GET', `${EQUIPMENT_PATHS.units}${query}`);
  return data.units;
}

export async function fetchCycles(unitId: string): Promise<readonly Cycle[]> {
  const data = await request<{ cycles: readonly Cycle[] }>(
    'GET',
    `${EQUIPMENT_PATHS.cycles}?unit_id=${encodeURIComponent(unitId)}`,
  );
  return data.cycles;
}

export async function createUnit(draft: UnitDraft): Promise<Unit> {
  const data = await request<{ unit: Unit }>('POST', EQUIPMENT_PATHS.units, draft);
  return data.unit;
}

export async function startProgram(unitId: string, program: string): Promise<Cycle> {
  const data = await request<{ cycle: Cycle }>('POST', EQUIPMENT_PATHS.start(unitId), { program });
  return data.cycle;
}

export async function runForecast(): Promise<void> {
  await request('POST', EQUIPMENT_PATHS.forecastRun);
}

/** Тон риска: порог сработки по умолчанию — 0,7. */
export function riskTone(risk: number | null | undefined): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (risk === null || risk === undefined) return 'neutral';
  if (risk >= 0.7) return 'danger';
  if (risk >= 0.4) return 'warn';
  return 'ok';
}
